import { getAttribution } from '@/utils/attribution';
import {
  type BookingPlatform,
  getApiPlatform,
  isWeb,
} from '@/utils/getPlatform';
import { isGoogleSourcedVisit } from '@/utils/rwg';

/**
 * How a booking is attributed: the platform it is recorded under, and the
 * marketing channel that produced it. Both compose the device with where the
 * visit came from, which is why they live together rather than inside either
 * source module.
 */

/** The marketing channels this app can resolve. A subset of squire-api's `marketingLeadTypes`. */
export type BookingChannel = 'Google';

/**
 * The platform a booking is recorded under: the device platform, unless the
 * visit came from a Reserve with Google link, in which case Google.
 *
 * Web only, deliberately. On native the platform value is the only thing
 * separating an iOS booking from an Android one, so overwriting it there would
 * destroy OS attribution on exactly the bookings being measured. Native Reserve
 * with Google handling is SQR-16104's call. SQR-16105.
 */
export const getBookingPlatform = (): BookingPlatform =>
  isWeb() && isGoogleSourcedVisit() ? 'Google' : getApiPlatform();

const hasGoogleAdsClick = (): boolean => {
  const attribution = getAttribution();
  return Boolean(
    attribution?.gclid || attribution?.gbraid || attribution?.wbraid,
  );
};

/**
 * The marketing channel to record against a new appointment, or `undefined`
 * when the visit carried no Google signal.
 *
 * Derived on demand from the two stores that already hold the evidence — the
 * Reserve with Google token (30-day window) and the Google Ads attribution
 * (90-day window) — rather than cached separately. A cached channel would
 * outlive whichever signal produced it and keep attributing bookings to Google
 * after that window closed.
 *
 * squire-api assigns the customer's marketing lead from this field
 * (`new_customer_service.js`); it does NOT infer one from the platform. Sending
 * only `name` is deliberate — squire-api defaults the channel's `shopId` to the
 * appointment's own shop, and the value flagship carries in its URLs is a shop
 * route, not the GUID that field validates against. SQR-16105.
 */
export const getBookingChannel = (): { name: BookingChannel } | undefined =>
  isGoogleSourcedVisit() || hasGoogleAdsClick()
    ? { name: 'Google' }
    : undefined;
